import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useMixpanel } from 'react-mixpanel-browser';
import {
  ChangeEvent,
  FC,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { State } from 'redux/initialState';
import { store } from 'redux/store';
import { SocietyAiPlanTier } from 'types/Society';
import { setModal } from 'modules/_shared/redux/modalActions';
import { useGetActualSociety } from 'hooks/useGetActualSociety';
import { useGetMenu } from 'modules/administratives/hooks/useGetMenu';
import cleanText from 'utils/cleanText';

import {
  appendAiMessage,
  clearAiConversation,
  getAiConversation,
  getAiConversations,
  getAiUsage,
  setAiHandoff,
} from 'modules/ai/redux/aiActions';
import { AI_EVENTS } from 'modules/ai/constants/aiAnalyticsEvents';
import { useAiChatStream } from 'modules/ai/hooks/useAiChatStream';
import { AiAgentNavigationState } from 'modules/ai/hooks/useNavigateToAiAgent';
import trackAiActivity from 'modules/ai/utils/trackAiActivity';
import AiQuotaExceededModal from 'modules/ai/modals/AiQuotaExceededModal';
import { AiQuotaExceededModalProvider } from 'modules/ai/modals/AiQuotaExceededModal/context/AiQuotaExceededModalContext';

import { SocietyAiAgentPageContextType } from '../types/SocietyAiAgentPageContextType';
import { SocietyAiAgentPageInitValue } from '../constants/societyAiAgentPageInitValue';
import { ApiProvider } from '../types/ApiProvider';
import { Message } from '../types/Message';
import { createMessageId } from '../utils/createMessageId';

const SocietyAiAgentPageContext = createContext<SocietyAiAgentPageContextType>(
  SocietyAiAgentPageInitValue
);

export const SocietyAiAgentPageProvider: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const { societyId } = useParams<{ societyId: string }>();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const mixpanel = useMixpanel();
  const pendingInitialNavigation = useRef<{
    message: string;
    autoSend: boolean;
  } | null>(null);
  const hasHandledInitialMessage = useRef(false);

  const [inputText, setInputText] = useState<string>('');
  const [selectedApi, setSelectedApi] = useState<ApiProvider>('anthropic');
  const [conversationId, setConversationId] = useState<string | null>(
    () => null
  );

  const aiMessages = useSelector(
    (state: State) => state.ai?.currentConversation?.messages ?? []
  );
  const aiConversations = useSelector(
    (state: State) => state.ai?.conversations ?? []
  );
  const activeConversationId = useSelector(
    (state: State) => state.ai?.currentConversation?.id ?? null
  );
  const usage = useSelector((state: State) => state.ai?.usage);
  const isHandoffActive = useSelector(
    (state: State) => state.ai?.isHandoff ?? false
  );
  const isStreamingFromRedux = useSelector(
    (state: State) => state.ai?.isStreaming ?? false
  );

  const { sendMessage, isStreaming, streamingText, error } = useAiChatStream();

  useGetActualSociety(societyId);
  useGetMenu(null);

  useEffect(() => {
    if (!societyId) return;

    const navigationState = location.state as AiAgentNavigationState | undefined;
    const isHandoff =
      navigationState?.handoff === true ||
      isHandoffActive ||
      (aiMessages.length > 0 && isStreamingFromRedux);

    hasHandledInitialMessage.current = isHandoff;
    pendingInitialNavigation.current = null;

    if (!isHandoff) {
      dispatch(clearAiConversation() as any);
      setConversationId(null);
    } else {
      setConversationId(activeConversationId);
      dispatch(setAiHandoff(false));
    }

    dispatch(getAiUsage(societyId) as any);
    dispatch(getAiConversations(societyId) as any);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, societyId]);

  useEffect(() => {
    if (activeConversationId && !conversationId) {
      setConversationId(activeConversationId);
    }
  }, [activeConversationId, conversationId]);

  useEffect(() => {
    const navigationState = location.state as AiAgentNavigationState | undefined;
    if (navigationState?.handoff) {
      history.replace({ pathname: location.pathname, search: location.search });
      return;
    }

    const message = navigationState?.initialMessage?.trim();
    if (!message) return;

    pendingInitialNavigation.current = {
      message,
      autoSend: navigationState?.autoSend !== false,
    };
    history.replace({ pathname: location.pathname, search: location.search });
  }, [history, location.pathname, location.search, location.state]);

  useEffect(() => {
    if (error === 'AI_QUOTA_EXCEEDED' && usage) {
      dispatch(
        setModal(
          <AiQuotaExceededModalProvider
            used={usage.used || 0}
            limit={usage.limit || 0}
            tier={usage.tier || SocietyAiPlanTier.FREE}
          >
            <AiQuotaExceededModal />
          </AiQuotaExceededModalProvider>
        )
      );
    }
  }, [dispatch, error, usage]);

  const messages: Message[] = useMemo(
    () =>
      aiMessages.map((m) => ({
        id: m.id,
        role: m.role,
        content: m.content,
        createdAt: m.createdAt ? new Date(m.createdAt) : new Date(),
        feedback: m.feedback ?? null,
      })),
    [aiMessages]
  );

  const resolveEntrySource = (): string => {
    const navigationState = location.state as AiAgentNavigationState | undefined;
    if (navigationState?.handoff) return 'embed_handoff';
    if (navigationState?.entrySource) return navigationState.entrySource;
    if (navigationState?.initialMessage) return 'direct_url';
    return 'direct_url';
  };

  const handleResetConversation = () => {
    const previousConversationId = activeConversationId;
    const previousMessageCount = aiMessages.length;

    trackAiActivity({
      mixpanel,
      state: store.getState() as State,
      eventName: AI_EVENTS.CONVERSATION_RESET,
      extras: {
        surface: 'agent_page',
        previousConversationId,
        previousMessageCount,
      },
    });

    dispatch(clearAiConversation() as any);
    if (societyId) dispatch(getAiConversations(societyId) as any);
    setConversationId(null);
    setInputText('');
  };

  const handleSelectConversation = (id: string) => {
    if (!societyId) return;

    const selected = aiConversations.find((c) => c.id === id);
    const conversationAgeDays = selected?.updatedAt
      ? Math.floor(
          (Date.now() - new Date(selected.updatedAt).getTime()) /
            (1000 * 60 * 60 * 24)
        )
      : null;

    trackAiActivity({
      mixpanel,
      state: store.getState() as State,
      eventName: AI_EVENTS.CONVERSATION_SELECTED,
      extras: {
        surface: 'agent_page',
        conversationId: id,
        messageCount: selected?.messageCount ?? 0,
        conversationAgeDays,
      },
    });

    dispatch(getAiConversation({ conversationId: id, societyId }) as any);
    setConversationId(id);
    setInputText('');
  };

  const sendUserMessage = async (message: string) => {
    const trimmed = message.trim();
    if (!trimmed || !societyId || isStreaming) return;

    const userMessageId = createMessageId('u');
    const assistantMessageId = createMessageId('a');
    const conversationTurn = Math.floor(aiMessages.length / 2) + 1;

    dispatch(
      appendAiMessage({
        id: userMessageId,
        role: 'user',
        content: trimmed,
        createdAt: new Date().toISOString(),
      })
    );
    setInputText('');

    try {
      await sendMessage({
        societyId,
        conversationId,
        message: trimmed,
        userMessageId,
        assistantMessageId,
        conversationTurn,
        isFirstMessage: conversationTurn === 1,
        analyticsContext: {
          entryPoint: 'agent_page',
          entrySource: resolveEntrySource(),
        },
        onConversationId: (id) => setConversationId(id),
        onDone: () => {
          if (societyId) dispatch(getAiConversations(societyId) as any);
        },
      });
    } catch {
      // quota exceeded handled via error effect above
    }
  };

  const handleChangeInputText = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setInputText(cleanText(event.target.value));
  };

  const handleChangeSelectedApi = (api: ApiProvider) => {
    setSelectedApi(api);
  };

  const handleSendMessage = async () => {
    await sendUserMessage(inputText);
  };

  useEffect(() => {
    const pending = pendingInitialNavigation.current;
    if (
      !pending ||
      !societyId ||
      isStreaming ||
      hasHandledInitialMessage.current
    ) {
      return;
    }

    hasHandledInitialMessage.current = true;
    pendingInitialNavigation.current = null;

    if (pending.autoSend) {
      sendUserMessage(pending.message);
      return;
    }

    setInputText(pending.message);
  }, [societyId, isStreaming]);

  return (
    <SocietyAiAgentPageContext.Provider
      value={{
        data: {
          inputText,
          selectedApi,
          apiOptions: [],
          messages,
          isThinking: isStreaming,
          streamingText,
          conversations: aiConversations,
          activeConversationId,
        },
        actions: {
          handleChangeInputText,
          handleResetConversation,
          handleSelectConversation,
          handleChangeSelectedApi,
          handleSendMessage,
        },
      }}
    >
      {children}
    </SocietyAiAgentPageContext.Provider>
  );
};

export const useSocietyAiAgentPage = () => {
  const context = useContext(SocietyAiAgentPageContext);
  if (!context) {
    throw new Error(
      'useSocietyAiAgentPage must be used within a SocietyAiAgentPageProvider'
    );
  }
  return context;
};
